import { z } from 'zod';
import { EVAL_TENANT_ID } from '@terra/api-common-constants';
import {
  hasNonEmptyStringArray,
  readTrimmedString,
} from './utils/schema-validation.utils';
import {
  EvaluationInferenceProviderIdSchema,
  EvaluationSlotPatchSchema,
  getRunScenarioInput,
} from './evaluation-run-shared.schemas';

const TriggerEvaluationRunRequestBaseSchema = z.object({
  applicationId: z.string().min(1).optional(),
  kind: z.enum(['RUN', 'SUITE', 'BATCH']).default('RUN'),
  title: z.string().min(1).max(200).optional(),
  sourceUrl: z.url().optional(),
  scenarioConfig: z.record(z.string(), z.unknown()).default({}),
  suiteFile: z.record(z.string(), z.unknown()).nullable().optional(),
  scenarioPath: z.string().min(1).optional(),
  uploadedScenarioId: z.string().min(1).optional(),
  branch: z.string().min(1).optional(),
  gitSource: z
    .object({
      repoUrl: z.url().optional(),
      branch: z.string().min(1).optional(),
      revision: z.string().min(1).optional(),
    })
    .optional(),
  traceId: z.string().min(1).optional(),
  signalIds: z.array(z.string().min(1)).max(200).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  scenarioSource: z.object({
    kind: z.literal('dataset'),
    datasetId: z.string().min(1),
    instantiationTenantId: z.literal(EVAL_TENANT_ID).default(EVAL_TENANT_ID),
  }),
  variants: z
    .array(
      z.object({
        name: z.string().min(1).max(100),
        engine: z.enum(['neo', 'agents-v2', 'claude-code']),
        modelOverlay: z
          .object({
            enabled: z.literal(true),
            accountId: z.string().min(1).max(200).optional(),
            providerId: EvaluationInferenceProviderIdSchema.optional(),
            crafts: z.record(z.string(), EvaluationSlotPatchSchema),
          })
          .optional(),
        accountId: z.string().min(1).max(200).optional(),
        providerId: EvaluationInferenceProviderIdSchema.optional(),
        applicationId: z.string().min(1).optional(),
        signalId: z.string().min(1).optional(),
        claudeCode: z
          .object({
            model: z.string().min(1).optional(),
          })
          .optional(),
        repeatCount: z.int().min(1).max(10).optional(),
        temperature: z.number().min(0).max(2).optional(),
      }),
    )
    .min(1)
    .max(32)
    .optional(),
});

export const TriggerEvaluationRunRequestSchema =
  TriggerEvaluationRunRequestBaseSchema.superRefine((value, context) => {
    if (value.kind !== 'RUN') {
      return;
    }

    const runScenarioInput = getRunScenarioInput(value.scenarioConfig);
    const isComponentScenario = Boolean(
      readTrimmedString(runScenarioInput?.componentId),
    );
    const isClaudeCodeScenario =
      runScenarioInput?.engine === 'claude-code' ||
      typeof runScenarioInput?.repoRef === 'string' ||
      value.variants?.some((variant) => variant.engine === 'claude-code');

    if (isClaudeCodeScenario || isComponentScenario) {
      return;
    }

    const hasTopLevelTraceId = Boolean(readTrimmedString(value.traceId));
    const hasTopLevelSignalIds = hasNonEmptyStringArray(value.signalIds);
    const hasScenarioTraceId = Boolean(
      readTrimmedString(runScenarioInput?.traceId),
    );
    const hasScenarioSignalId = Boolean(
      readTrimmedString(runScenarioInput?.signalId),
    );
    const hasScenarioSignalIds = hasNonEmptyStringArray(
      runScenarioInput?.signalIds,
    );

    const hasTraceId = hasTopLevelTraceId || hasScenarioTraceId;
    const hasSignalId =
      hasTopLevelSignalIds || hasScenarioSignalId || hasScenarioSignalIds;

    if (!hasTraceId && !hasSignalId) {
      return;
    }

    if (hasTraceId === hasSignalId) {
      context.addIssue({
        code: 'custom',
        message:
          'RUN requires exactly one execution source: traceId or signalIds',
      });
    }
  });

export const TriggerEvaluationRunResponseSchema = z.object({
  runId: z.string(),
  queued: z.boolean(),
  matrixId: z.string().optional(),
  variantRunIds: z.array(z.string()).optional(),
});
